import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, inject } from '@angular/core'
import { CommonModule } from '@angular/common'
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser'

@Component({
  selector: 'app-document-viewer',
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (visible) {
      <!-- Backdrop -->
      <div
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
        role="button"
        tabindex="0"
        (click)="onBackdropClick()"
        (keydown.escape)="viewerClose.emit()"
      >
        <!-- Modal -->
        <div
          class="relative bg-white rounded-2xl shadow-2xl flex flex-col w-full max-w-4xl mx-4"
          style="height: 90vh; max-height: 90vh"
          role="dialog"
          aria-modal="true"
          [attr.aria-label]="filename"
          (click)="$event.stopPropagation()"
          (keydown)="$event.stopPropagation()"
        >
          <!-- Header -->
          <div class="flex items-center gap-3 px-6 py-4 border-b border-gray-200 flex-shrink-0">
            <!-- File icon badge -->
            <span
              class="flex-shrink-0 text-xs font-bold uppercase px-2 py-0.5 rounded"
              [ngClass]="isImage ? 'bg-purple-100 text-purple-700' : 'bg-red-100 text-red-700'"
            >
              {{ fileTypeBadge }}
            </span>
            <h2 class="flex-1 text-primary-500 font-semibold text-base truncate" [title]="filename">
              {{ filename }}
            </h2>
            <!-- Close button -->
            <button
              type="button"
              (click)="viewerClose.emit()"
              class="p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200 text-gray-500 hover:text-gray-800"
              title="Fermer"
            >
              <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
              </svg>
            </button>
          </div>

          <!-- Body -->
          <div class="flex-1 relative bg-gray-50 rounded-b-2xl overflow-hidden">
            <!-- Loading -->
            @if (loading) {
              <div class="absolute inset-0 flex flex-col items-center justify-center gap-3 text-gray-400">
                <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
                <span class="text-sm">Chargement du document…</span>
              </div>
            }

            <!-- Error -->
            @if (!loading && error) {
              <div class="absolute inset-0 flex flex-col items-center justify-center gap-3 text-red-500 px-8 text-center">
                <svg class="w-10 h-10" viewBox="0 0 24 24" fill="none">
                  <path d="M12 9V13M12 17H12.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                  <path
                    d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                    stroke="currentColor"
                    stroke-width="2"
                  />
                </svg>
                <p class="text-sm font-medium">Impossible de charger le document.</p>
              </div>
            }

            <!-- PDF viewer -->
            @if (!loading && !error && safeBlobUrl && isPdf) {
              <iframe
                [src]="safeBlobUrl"
                class="absolute inset-0 w-full h-full rounded-b-2xl"
                style="border: none"
                title="Aperçu du document"
              ></iframe>
            }

            <!-- Image viewer -->
            @if (!loading && !error && safeBlobUrl && isImage) {
              <div class="absolute inset-0 flex items-center justify-center p-4">
                <img
                  [src]="safeBlobUrl"
                  [alt]="filename"
                  class="max-w-full max-h-full object-contain rounded-lg shadow"
                />
              </div>
            }
          </div>
        </div>
      </div>
    }
  `,
})
export class DocumentViewerComponent implements OnChanges {
  @Input() visible = false
  @Input() blobUrl: string | null = null
  @Input() mimeType: string | null = null
  @Input() filename = 'Document'
  @Input() loading = false
  @Input() error = false
  @Output() viewerClose = new EventEmitter<void>()
  @Output() documentDownload = new EventEmitter<void>()

  safeBlobUrl: SafeResourceUrl | null = null

  private readonly sanitizer = inject(DomSanitizer)

  get isPdf(): boolean {
    return this.mimeType === 'application/pdf'
  }

  get isImage(): boolean {
    return !!this.mimeType?.startsWith('image/')
  }

  get fileTypeBadge(): string {
    if (this.isPdf) return 'PDF'
    const sub = this.mimeType?.split('/')[1]?.toUpperCase()
    return sub ?? 'Fichier'
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['blobUrl']) {
      this.safeBlobUrl = this.blobUrl ? this.sanitizer.bypassSecurityTrustResourceUrl(this.blobUrl) : null
    }
  }

  onBackdropClick(): void {
    this.viewerClose.emit()
  }
}
